import type { NotificacionBuzonDTO } from '../../services/buzonService';
import { canOpenMaterialidadModule } from '../../permissions/materialidad-module-access';
import { TIPO_EVENTO_BUZON, normalizarClaveTipoEvento } from './categoriasBuzon';

/**
 * Registro al que lleva una notificación del buzón. `null` = la notificación se muestra sin enlace.
 * - `factura`: el detalle de la factura (`IdEntidad` = IdFactura del portal).
 * - `confirmar-pendiente`: la confirmación de un registro de correo ambiguo (PES-762); lleva a ella tanto
 *   el aviso al remitente como el escalamiento al administrador de la empresa.
 * - `efirma`: la pantalla de e.firma de la empresa.
 * - `solicitudBorrado`: la pestaña "Solicitudes de borrado" de "Por revisar" con la solicitud abierta
 *   (`IdEntidad` = Id de la solicitud). Solo para quien atiende.
 * - `solicitudReasignacion`: la bandeja de solicitudes de reasignación, abierta en esa solicitud
 *   (`IdEntidad` = IdSolicitud; solo el administrador).
 * - `cargarFactura`: la pantalla de carga de facturas (acción secundaria de una factura cancelada en el SAT).
 * - `usuariosEliminados`: la pestaña «Por usuarios eliminados» de Solicitudes de reasignación (`TipoEntidad` `UsuarioBaja`,
 *   `IdEntidad` = Id del usuario eliminado; solo el administrador).
 */
export type EnlaceNotificacion =
  | { tipo: 'factura'; idFactura: number }
  | { tipo: 'confirmar-pendiente'; idFactura: number }
  | { tipo: 'efirma' }
  | { tipo: 'solicitudBorrado'; idSolicitud: number }
  | { tipo: 'solicitudReasignacion'; idSolicitud: number }
  | { tipo: 'usuariosEliminados' }
  | { tipo: 'cargarFactura' }
  | null;

/** Permisos que deciden si una notificación lleva enlace. Salen de `useRolePermissions`. */
export interface PermisosEnlaceBuzon {
  isUser: boolean;
  canViewInvoices: boolean;
  canViewInvoicesVisor: boolean;
  canViewListInvoices: boolean;
  canViewListAllInvoices: boolean;
  canViewDetailInvoices: boolean;
  canViewDetailVisor: boolean;
  canViewCompanyEFirmas: boolean;
  /** FlujoBorrado: atiende solicitudes de borrado (`AtenderSolicitudBorrado` o rol Empresa). */
  canAttendDeleteRequest?: boolean;
  /** Roles Empresa y Operativo: abren la confirmación de un registro de correo ambiguo. */
  canViewPendientesConfirmar: boolean;
  /** Actividad `VerRevisionMaterialidad`; junto con las banderas de listado decide el acceso al módulo (`canOpenMaterialidadModule`). */
  canViewRevisionMaterialidad: boolean;
  /** Actividad `CargarFactura`: habilita la acción "Cargar factura nueva" de una cancelada. Opcional. */
  canLoadInvoices?: boolean;
  /** Bandeja de solicitudes de reasignación (rol Empresa). Opcional. */
  canViewSolicitudesReasignacion?: boolean;
}

/** Tipos del buzón del borrado controlado (los siembra `BZ009` del SSO). */
export const TIPOS_BUZON_BORRADO = {
  pendiente: 'SOLICITUD_BORRADO',
  aprobada: 'SOLICITUD_BORRADO_APROBADA',
  negada: 'SOLICITUD_BORRADO_NEGADA',
} as const;

/** Ruta del apartado de solicitudes de borrado, con la solicitud a abrir. */
export const rutaSolicitudBorrado = (idSolicitud: number): string =>
  `/materialidad/por-revisar?pestana=borrado&solicitud=${idSolicitud}`;

/** `Datos` del aviso de borrado: `{ TipoRegistro, IdRegistro }`. */
const registroDelAvisoBorrado = (datos: unknown): { tipo: string; id: number } | null => {
  if (!datos || typeof datos !== 'object') return null;
  const { TipoRegistro, IdRegistro } = datos as { TipoRegistro?: unknown; IdRegistro?: unknown };
  const id = Number(IdRegistro);
  return typeof TipoRegistro === 'string' && Number.isInteger(id) && id > 0 ? { tipo: TipoRegistro, id } : null;
};

/**
 * @function puedeAbrirFacturas
 * @description Si el usuario puede abrir el detalle de una factura por alguno de los dos caminos que
 * ya tiene la tabla de facturas: su listado (ListadoFacturas + ver detalle) o el visor (FacturasVisor
 * + ver detalle de visor). Hoy solo el rol Operativo entra a Materialidad; el rol Empresa recibe los
 * avisos de cambios en facturas pero no abre el detalle, así que sus notificaciones van sin enlace.
 */
export const puedeAbrirFacturas = (p: PermisosEnlaceBuzon): boolean =>
  p.isUser &&
  (((p.canViewInvoices || p.canViewListInvoices) && p.canViewDetailInvoices) ||
    ((p.canViewInvoicesVisor || p.canViewListAllInvoices) && p.canViewDetailVisor));

/** Claves de los avisos accionables de un registro de correo ambiguo pendiente de confirmar (SSO, migraciones BZ010 y BZ011). */
const CLAVES_CONFIRMAR_PENDIENTE: ReadonlySet<string> = new Set([
  'PENDIENTE_CONFIRMAR_REGISTRO',
  'PENDIENTE_CONFIRMAR_ESCALADO',
]);

/**
 * @function normalizarTipoEntidad
 * @description `TipoEntidad` sin espacios a los lados y en minúsculas, para comparar sin depender de
 * cómo lo escribió quien registró la notificación ("Factura", " FACTURA "). Vacío si no vino.
 */
export const normalizarTipoEntidad = (tipoEntidad: string | null | undefined): string =>
  (tipoEntidad ?? '').trim().toLowerCase();

/**
 * @function resolverEnlaceNotificacion
 * @description Decide a qué registro lleva una notificación, con `Modulo` + `TipoEntidad` +
 * `IdEntidad` (el SSO no conoce las rutas del front). Si el usuario no tiene permiso para ese
 * registro, o el tipo no se conoce, la notificación se muestra sin enlace en lugar de llevar a una
 * pantalla que lo regresaría al inicio.
 * FACTURA_NO_RECONOCIDA_CANCELADA (PES-397) apunta a una fila de la conciliación (`TipoEntidad` `FacturaSat`), no a
 * una factura del portal: no tiene detalle que abrir y va sin enlace. Igual FACTURA_NO_RECONOCIDA_PROVEEDOR_NOTIFICADO
 * (aviso de que se pidió por correo al proveedor cancelar la factura): también apunta a `FacturaSat`, sin enlace.
 * TODO(marbella): confirmar si ese aviso debe llevar a la pantalla de conciliación (`docs/BUZON.md` lo sugiere;
 * el boceto no lo hace porque el aviso es informativo y no hay fila que resaltar).
 */
export const resolverEnlaceNotificacion = (
  notificacion: Pick<NotificacionBuzonDTO, 'Modulo' | 'TipoEntidad' | 'IdEntidad'> &
    Partial<Pick<NotificacionBuzonDTO, 'ClaveTipoEvento' | 'Datos'>>,
  permisos: PermisosEnlaceBuzon
): EnlaceNotificacion => {
  const tipoEntidad = normalizarTipoEntidad(notificacion.TipoEntidad);

  // "Ya no eres el responsable": la factura salió de los listados del usuario, así que abrirla solo
  // lo llevaría a un aviso de "ya no está en tus listados". Se muestra sin enlace.
  if (normalizarClaveTipoEvento(notificacion.ClaveTipoEvento) === TIPO_EVENTO_BUZON.FacturaReasignada) {
    return null;
  }

  // FlujoBorrado: el pendiente lleva a atenderlo; la negada, a la factura (sigue existiendo); la aprobada, a
  // ningún lado (el registro ya se borró).
  if (tipoEntidad === 'solicitudborrado') {
    const clave = (notificacion.ClaveTipoEvento ?? '').trim().toUpperCase();
    if (clave === TIPOS_BUZON_BORRADO.negada) {
      const registro = registroDelAvisoBorrado(notificacion.Datos);
      return registro && registro.tipo === 'Factura' && puedeAbrirFacturas(permisos)
        ? { tipo: 'factura', idFactura: registro.id }
        : null;
    }
    if (clave === TIPOS_BUZON_BORRADO.aprobada) return null;
    const idSolicitud = Number(notificacion.IdEntidad);
    return Number.isInteger(idSolicitud) && idSolicitud > 0 && permisos.canAttendDeleteRequest
      ? { tipo: 'solicitudBorrado', idSolicitud }
      : null;
  }

  if (tipoEntidad === 'factura' && (notificacion.Modulo ?? '').trim().toLowerCase() === 'materialidad') {
    const idFactura = Number(notificacion.IdEntidad);
    if (!Number.isInteger(idFactura) || idFactura <= 0) {
      return null;
    }
    // Los dos avisos de confirmación llevan a su pantalla y no al detalle: ahí se confirma o se corrige.
    // Esa pantalla vive bajo /materialidad/*, que exige además el acceso al módulo (la misma regla con la
    // que el enlace /facturas/:token decide); sin él se va sin enlace en lugar de rebotar al inicio.
    if (CLAVES_CONFIRMAR_PENDIENTE.has((notificacion.ClaveTipoEvento ?? '').trim().toUpperCase())) {
      return permisos.canViewPendientesConfirmar && canOpenMaterialidadModule(permisos)
        ? { tipo: 'confirmar-pendiente', idFactura }
        : null;
    }
    return puedeAbrirFacturas(permisos) ? { tipo: 'factura', idFactura } : null;
  }

  if (tipoEntidad === 'solicitudreasignacion' && (notificacion.Modulo ?? '').trim().toLowerCase() === 'materialidad') {
    const idSolicitud = Number(notificacion.IdEntidad);
    if (!Number.isInteger(idSolicitud) || idSolicitud <= 0 || permisos.canViewSolicitudesReasignacion !== true) {
      return null;
    }
    return { tipo: 'solicitudReasignacion', idSolicitud };
  }

  if (tipoEntidad === 'usuariobaja' && (notificacion.Modulo ?? '').trim().toLowerCase() === 'materialidad') {
    return permisos.canViewSolicitudesReasignacion === true ? { tipo: 'usuariosEliminados' } : null;
  }

  if (tipoEntidad === 'efirma') {
    return permisos.canViewCompanyEFirmas ? { tipo: 'efirma' } : null;
  }

  return null;
};

/**
 * @function resolverEnlaceSecundario
 * @description Segunda acción de una notificación, además de su enlace principal. Hoy solo la de una
 * factura cancelada en el SAT sin sustituta (FACTURA_CANCELADA_SAT): "Cargar factura nueva", para quien
 * puede cargar facturas. Al cargar la vigente, la cancelada queda como su materialidad y el aviso se cierra.
 */
export const resolverEnlaceSecundario = (
  notificacion: Partial<Pick<NotificacionBuzonDTO, 'ClaveTipoEvento'>>,
  permisos: PermisosEnlaceBuzon
): EnlaceNotificacion =>
  normalizarClaveTipoEvento(notificacion.ClaveTipoEvento) === TIPO_EVENTO_BUZON.FacturaCanceladaSat &&
  permisos.isUser &&
  permisos.canLoadInvoices === true
    ? { tipo: 'cargarFactura' }
    : null;

/** Texto del botón del enlace, según el registro. */
export const textoEnlaceNotificacion = (enlace: NonNullable<EnlaceNotificacion>): string => {
  switch (enlace.tipo) {
    case 'factura':
      return 'Abrir factura';
    case 'confirmar-pendiente':
      return 'Confirmar registro';
    case 'cargarFactura':
      return 'Cargar factura nueva';
    case 'solicitudReasignacion':
    case 'solicitudBorrado':
      return 'Atender solicitud';
    case 'usuariosEliminados':
      return 'Reasignar facturas';
    default:
      return 'Ir a e.firma';
  }
};
